import { createFileRoute, Link } from '@tanstack/react-router'
import { Check, Copy, ExternalLink } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { API_OVERVIEW_SECTIONS } from '@/components/docs/ApiSidebarToc'
import { Badge } from '@/components/ui/badge'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import ArticleCta from '@/content/ArticleCta'
import type { FieldDoc, ParamDoc } from '@/lib/docs/api-reference'
import type {
  ApiReference,
  Highlighted,
  OperationView,
  SectionView,
} from '@/lib/docs/api-reference.server'
import { getApiReference } from '@/lib/docs/server'
import { pageLinks, pageMeta } from '@/lib/seo'
import { cn } from '@/lib/utils'

/**
 * The API reference. Rendered from code, not the CMS: the sections and field
 * tables come from the same zod schemas the routes validate with (via the
 * OpenAPI document), so the page cannot drift from the contract, and every
 * example response is checked against those schemas in tests.
 */
export const Route = createFileRoute('/docs/api')({
  loader: () => getApiReference(),
  // The reference is static per deploy; never refetch it on navigation.
  staleTime: Number.POSITIVE_INFINITY,
  head: () => ({
    meta: pageMeta({
      title: 'API reference · Masklabs',
      description:
        'Reference for the Masklabs API: authentication, conventions, errors, and every endpoint with examples in curl, Python, and Node.js.',
      path: '/docs/api',
    }),
    links: pageLinks('/docs/api'),
  }),
  component: ApiReferencePage,
})

function ApiReferencePage() {
  const ref = Route.useLoaderData()
  return (
    <div className="w-full">
      <Breadcrumb className="mb-6">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/">Home</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/docs">Docs</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>API reference</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <header className="max-w-3xl">
        <p className="text-sm font-medium text-primary">API</p>
        <h1 className="mt-2 text-3xl font-bold tracking-tight text-balance sm:text-4xl">
          API reference
        </h1>
        <p className="mt-3 text-base text-muted-foreground text-pretty">
          Manage organizations, proxy credentials, and usage over HTTP.
        </p>
        <div className="mt-5 flex flex-wrap items-center gap-3 text-sm">
          <code className="rounded-md border bg-muted/50 px-2.5 py-1 font-mono text-[13px]">
            {ref.baseUrl}
          </code>
          <a
            href={ref.openApiUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-primary underline-offset-4 hover:underline"
          >
            OpenAPI document (JSON)
            <ExternalLink className="size-3.5" />
          </a>
        </div>
      </header>

      <PageNav sections={ref.sections} />

      <div className="mt-12 flex flex-col gap-16">
        <Overview ref_={ref} />
        <Quickstart ref_={ref} />
        <Authentication />
        <Conventions ref_={ref} />
        <Errors ref_={ref} />
        {ref.sections.map((section) => (
          <EndpointSection key={section.tag} section={section} />
        ))}
      </div>

      <ArticleCta heading="Get started" className="mt-16" />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Navigation
// ---------------------------------------------------------------------------

function PageNav({ sections }: { sections: SectionView[] }) {
  return (
    <nav
      aria-label="On this page"
      className="mt-8 flex flex-wrap gap-2 border-y py-4 lg:hidden"
    >
      {API_OVERVIEW_SECTIONS.map((s) => (
        <a
          key={s.id}
          href={`#${s.id}`}
          className="rounded-full border px-3 py-1 text-sm text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground"
        >
          {s.title}
        </a>
      ))}
      <span aria-hidden className="mx-1 hidden self-center border-l sm:block" />
      {sections.map((s) => (
        <a
          key={s.tag}
          href={`#${s.anchor}`}
          className="rounded-full border px-3 py-1 text-sm text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground"
        >
          {s.tag}
        </a>
      ))}
    </nav>
  )
}

// ---------------------------------------------------------------------------
// Prose sections
// ---------------------------------------------------------------------------

function Prose({
  id,
  title,
  children,
}: {
  id: string
  title: string
  children: React.ReactNode
}) {
  return (
    <section id={id} className="scroll-mt-24">
      <h2 className="text-2xl font-bold tracking-tight">{title}</h2>
      <div className="prose prose-neutral mt-4 max-w-4xl dark:prose-invert">
        {children}
      </div>
    </section>
  )
}

function Overview({ ref_ }: { ref_: ApiReference }) {
  return (
    <Prose id="overview" title="Overview">
      <p>
        The API covers organizations, proxy credentials, and usage. Proxy
        traffic does not go through it; see the{' '}
        <Link to="/docs/$slug" params={{ slug: 'quickstart' }}>
          quickstart
        </Link>{' '}
        for connecting to the proxy.
      </p>
      <p>
        Base URL: <code>{ref_.baseUrl}</code>. The full contract is available as
        an <a href={ref_.openApiUrl}>OpenAPI document</a> for client generators.
      </p>
    </Prose>
  )
}

function Quickstart({ ref_ }: { ref_: ApiReference }) {
  return (
    <Prose id="quickstart" title="Quick start">
      <ol>
        <li>
          Create a key under API keys on your{' '}
          <Link to="/account">Account page</Link>. It is shown once.
        </li>
        <li>
          List your organizations. Each has an <code>id</code> used in the other
          endpoints.
        </li>
      </ol>
      <div className="not-prose mt-4">
        <SampleTabs samples={ref_.quickstart} />
      </div>
    </Prose>
  )
}

function Authentication() {
  return (
    <Prose id="authentication" title="Authentication">
      <p>
        Send the key in the <code>Authorization</code> header:{' '}
        <code>Authorization: Bearer masklabs_…</code>. Keys are created and
        revoked on the <Link to="/account">Account page</Link>.
      </p>
      <p>
        A key has the access of the user who created it: every organization that
        user belongs to, with that user&apos;s role in each. Owners and admins
        can delete credentials and rotate passwords; members can create, rename,
        limit, enable, and disable them, and rotate their sticky IP. If the user
        is removed from an organization, their keys lose access to it. See{' '}
        <Link to="/docs/$slug" params={{ slug: 'orgs-users-credentials' }}>
          Organizations, users, and credentials
        </Link>
        .
      </p>
      <p>
        A key can be limited to one organization when it is created. Requests
        for other organizations return <code>403 forbidden</code>.
      </p>
    </Prose>
  )
}

function Conventions({ ref_ }: { ref_: ApiReference }) {
  return (
    <Prose id="conventions" title="Conventions">
      <ul>
        <li>Data is in bytes. 1 GB = 1,000,000,000 bytes.</li>
        <li>
          Timestamps are ISO-8601 in UTC. Usage is grouped by UTC date, written{' '}
          <code>YYYY-MM-DD</code>.
        </li>
        <li>
          Usage endpoints take <code>from</code> and <code>to</code> dates. The
          default range is the last {ref_.usageWindow.defaultDays} days and the
          maximum is {ref_.usageWindow.maxDays}. <code>from</code> is moved
          forward to the creation date when the range starts earlier, so a
          series never includes days before the organization or credential
          existed. The current day is incomplete until it ends;{' '}
          <code>as_of</code> is when the usage data was last updated.
        </li>
        <li>
          The sum of per-credential usage can differ slightly from the
          organization total. Billing is based on the organization total.
        </li>
        <li>
          Rate limit: {ref_.rateLimitPerMinute} requests per minute per key.
          Responses include <code>X-RateLimit-Limit</code> and{' '}
          <code>X-RateLimit-Remaining</code>. Over the limit, requests return{' '}
          <code>429</code> with <code>Retry-After</code> in seconds.
        </li>
        <li>
          Responses are sent with <code>Cache-Control: no-store</code>.
        </li>
        <li>
          A credential&apos;s password is returned only by the create and rotate
          endpoints. It cannot be read later.
        </li>
      </ul>
    </Prose>
  )
}

function Errors({ ref_ }: { ref_: ApiReference }) {
  return (
    <Prose id="errors" title="Errors">
      <p>
        Errors return an <code>error</code> object with a <code>code</code> and
        a <code>message</code>. Validation errors also include{' '}
        <code>details</code> with one entry per failed field.
      </p>
      <div className="not-prose my-6">
        <CodeBlock block={ref_.errorExample} label="400 Bad Request" />
      </div>
      <div className="not-prose overflow-x-auto rounded-xl border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left text-xs font-semibold tracking-wide text-muted-foreground uppercase">
            <tr>
              <th className="px-4 py-2.5">Code</th>
              <th className="px-4 py-2.5">Meaning</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {ref_.errorCodes.map((e) => (
              <tr key={e.code}>
                <td className="px-4 py-2.5 align-top font-mono text-[13px]">
                  {e.code}
                </td>
                <td className="px-4 py-2.5 text-muted-foreground">
                  <InlineCode text={e.meaning} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Prose>
  )
}

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

function EndpointSection({ section }: { section: SectionView }) {
  return (
    <section id={section.anchor} className="scroll-mt-24">
      <div className="max-w-3xl">
        <h2 className="text-2xl font-bold tracking-tight">{section.tag}</h2>
        {section.description ? (
          <p className="mt-2 text-muted-foreground text-pretty">
            {section.description}
          </p>
        ) : null}
        <ul className="mt-4 flex flex-col gap-1.5">
          {section.operations.map((op) => (
            <li key={op.id}>
              <a
                href={`#${op.anchor}`}
                className="group flex flex-wrap items-center gap-x-2.5 gap-y-1 text-sm"
              >
                <MethodBadge method={op.method} />
                <code className="font-mono text-[13px] break-all text-foreground group-hover:underline">
                  {op.path}
                </code>
                <span className="hidden text-muted-foreground sm:inline">
                  {op.summary}
                </span>
              </a>
            </li>
          ))}
        </ul>
      </div>
      <div className="mt-8 flex flex-col divide-y">
        {section.operations.map((op) => (
          <Operation key={op.id} op={op} />
        ))}
      </div>
    </section>
  )
}

function Operation({ op }: { op: OperationView }) {
  const pathParams = op.params.filter((p) => p.in === 'path')
  const queryParams = op.params.filter((p) => p.in === 'query')
  const failures = op.responses.filter((r) => !r.status.startsWith('2'))
  return (
    <article id={op.anchor} className="scroll-mt-24 py-10 first:pt-6">
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,5fr)_minmax(0,6fr)] lg:gap-10">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2.5">
            <MethodBadge method={op.method} />
            <code className="font-mono text-sm break-all">{op.path}</code>
          </div>
          <h3 className="mt-3 text-xl font-semibold tracking-tight">
            {op.summary}
          </h3>
          {op.description ? (
            <p className="mt-2 text-muted-foreground text-pretty">
              <InlineCode text={op.description} />
            </p>
          ) : null}

          {pathParams.length > 0 ? (
            <Block title="Path parameters">
              <ParamList params={pathParams} />
            </Block>
          ) : null}
          {queryParams.length > 0 ? (
            <Block title="Query parameters">
              <ParamList params={queryParams} />
            </Block>
          ) : null}
          {op.body ? (
            <Block
              title="Request body"
              aside={op.body.required ? 'required' : 'optional'}
            >
              <FieldList fields={op.body.fields} />
            </Block>
          ) : null}
          <Block
            title="Response"
            aside={
              op.success
                ? `${op.success.status}${op.success.schemaName ? ` · ${op.success.schemaName}` : ''}`
                : undefined
            }
          >
            {op.success?.fields && op.success.fields.length > 0 ? (
              <FieldList fields={op.success.fields} />
            ) : (
              <p className="text-sm text-muted-foreground">
                {op.success?.description ?? 'No body.'}
              </p>
            )}
          </Block>
          {failures.length > 0 ? (
            <Block title="Errors">
              <ul className="flex flex-col gap-1.5 text-sm">
                {failures.map((r) => (
                  <li key={r.status} className="flex gap-3">
                    <code className="w-8 shrink-0 font-mono text-[13px]">
                      {r.status}
                    </code>
                    <span className="text-muted-foreground">
                      <InlineCode text={r.description} />
                    </span>
                  </li>
                ))}
              </ul>
            </Block>
          ) : null}
        </div>

        <div className="min-w-0 lg:sticky lg:top-24 lg:self-start">
          <SampleTabs samples={op.samples} />
          {op.exampleResponse ? (
            <div className="mt-4">
              {op.exampleResponse.body ? (
                <CodeBlock
                  block={op.exampleResponse.body}
                  label={`Response · ${op.exampleResponse.status}`}
                  maxHeight
                />
              ) : (
                <div className="rounded-xl border px-4 py-3 text-sm text-muted-foreground">
                  Response · {op.exampleResponse.status}, no body.
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </article>
  )
}

function Block({
  title,
  aside,
  children,
}: {
  title: string
  aside?: string
  children: React.ReactNode
}) {
  return (
    <div className="mt-6">
      <div className="flex items-baseline justify-between gap-3 border-b pb-2">
        <h4 className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
          {title}
        </h4>
        {aside ? (
          <span className="font-mono text-xs text-muted-foreground">
            {aside}
          </span>
        ) : null}
      </div>
      <div className="mt-3">{children}</div>
    </div>
  )
}

function ParamList({ params }: { params: ParamDoc[] }) {
  return (
    <ul className="flex flex-col divide-y">
      {params.map((p) => (
        <li key={p.name} className="py-2.5 first:pt-0 last:pb-0">
          <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
            <code className="font-mono text-[13px] font-medium">{p.name}</code>
            <span className="font-mono text-xs text-muted-foreground">
              {p.type}
            </span>
            {p.required ? (
              <span className="text-xs text-primary">required</span>
            ) : null}
          </div>
          {p.description ? (
            <p className="mt-1 text-sm text-muted-foreground text-pretty">
              <InlineCode text={p.description} />
            </p>
          ) : null}
        </li>
      ))}
    </ul>
  )
}

function FieldList({
  fields,
  nested = false,
}: {
  fields: FieldDoc[]
  nested?: boolean
}) {
  return (
    <ul
      className={cn('flex flex-col divide-y', nested && 'mt-2 border-l pl-4')}
    >
      {fields.map((f) => (
        <li key={f.name} className="py-2.5 first:pt-0 last:pb-0">
          <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
            <code className="font-mono text-[13px] font-medium">{f.name}</code>
            <span className="font-mono text-xs text-muted-foreground">
              {f.type}
              {f.nullable ? ' | null' : ''}
            </span>
            {f.required ? null : (
              <span className="text-xs text-muted-foreground">optional</span>
            )}
          </div>
          {f.description ? (
            <p className="mt-1 text-sm text-muted-foreground text-pretty">
              <InlineCode text={f.description} />
            </p>
          ) : null}
          {f.enumValues ? (
            <p className="mt-1 flex flex-wrap gap-1.5">
              {f.enumValues.map((v) => (
                <code
                  key={v}
                  className="rounded border bg-muted/50 px-1.5 py-0.5 font-mono text-xs"
                >
                  {v}
                </code>
              ))}
            </p>
          ) : null}
          {f.children ? <FieldList fields={f.children} nested /> : null}
        </li>
      ))}
    </ul>
  )
}

// ---------------------------------------------------------------------------
// Code
// ---------------------------------------------------------------------------

const SAMPLE_TABS: { id: 'curl' | 'python' | 'node'; label: string }[] = [
  { id: 'curl', label: 'curl' },
  { id: 'python', label: 'Python' },
  { id: 'node', label: 'Node.js' },
]

function SampleTabs({
  samples,
}: {
  samples: Record<'curl' | 'python' | 'node', Highlighted>
}) {
  const [active, setActive] = useState<'curl' | 'python' | 'node'>('curl')
  return (
    <Tabs
      value={active}
      onValueChange={(v) => setActive(v as typeof active)}
      className="gap-2"
    >
      <div className="flex items-center justify-between gap-2">
        <TabsList className="h-8">
          {SAMPLE_TABS.map((t) => (
            <TabsTrigger key={t.id} value={t.id} className="px-2.5 text-xs">
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
        <CopyButton code={samples[active].code} />
      </div>
      {SAMPLE_TABS.map((t) => (
        <TabsContent key={t.id} value={t.id}>
          <CodeBlock block={samples[t.id]} />
        </TabsContent>
      ))}
    </Tabs>
  )
}

function CodeBlock({
  block,
  label,
  maxHeight = false,
}: {
  block: Highlighted
  /** Header text; the copy button lives in the header when one is shown. */
  label?: string
  maxHeight?: boolean
}) {
  return (
    <div className="overflow-hidden rounded-xl border bg-card">
      {label ? (
        <div className="flex items-center justify-between border-b py-1 pr-1 pl-4 font-mono text-xs text-muted-foreground">
          {label}
          <CopyButton code={block.code} />
        </div>
      ) : null}
      {/* Shiki output from our own generated samples (dual theme, see the
          .shiki rules in styles.css). */}
      <div
        className={cn(
          'text-[13px] leading-relaxed [&_pre]:overflow-x-auto [&_pre]:p-4',
          maxHeight && '[&_pre]:max-h-[28rem] [&_pre]:overflow-y-auto',
        )}
        // biome-ignore lint/security/noDangerouslySetInnerHtml: trusted Shiki output generated from our own API contract
        dangerouslySetInnerHTML={{ __html: block.html }}
      />
    </div>
  )
}

function CopyButton({ code }: { code: string }) {
  const [copied, setCopied] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined)
  useEffect(() => () => clearTimeout(timer.current), [])
  return (
    <Button
      variant="ghost"
      size="icon"
      className="size-7"
      aria-label="Copy code"
      onClick={async () => {
        await navigator.clipboard.writeText(code)
        setCopied(true)
        clearTimeout(timer.current)
        timer.current = setTimeout(() => setCopied(false), 1500)
      }}
    >
      {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
    </Button>
  )
}

const METHOD_STYLES: Record<string, string> = {
  GET: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
  POST: 'bg-sky-500/10 text-sky-700 dark:text-sky-400',
  PATCH: 'bg-amber-500/10 text-amber-700 dark:text-amber-400',
  DELETE: 'bg-destructive/10 text-destructive',
}

function MethodBadge({ method }: { method: string }) {
  return (
    <Badge
      variant="outline"
      className={cn(
        'border-transparent font-mono text-[11px] font-semibold',
        METHOD_STYLES[method] ?? 'bg-muted text-foreground',
      )}
    >
      {method}
    </Badge>
  )
}

/** Render `code spans` inside a plain description string. */
function InlineCode({ text }: { text: string }) {
  const parts = text.split(/(`[^`]+`)/g)
  return (
    <>
      {parts.map((part, i) =>
        part.startsWith('`') && part.endsWith('`') ? (
          <code
            // biome-ignore lint/suspicious/noArrayIndexKey: static split of a constant string
            key={i}
            className="rounded bg-muted/60 px-1 py-0.5 font-mono text-[0.85em]"
          >
            {part.slice(1, -1)}
          </code>
        ) : (
          part
        ),
      )}
    </>
  )
}
